/**
 * 异步数据加载 hook —— 把「加载中 / 成功 / 失败」做成穷尽的联合类型。
 *
 * 用联合类型而不是 `{ loading, data, error }` 三个独立字段，是为了让
 * 「loading=false 且 data=undefined 且 error=undefined」这种不可能状态在类型层就不存在，
 * 从而堵死「什么都不渲染的白屏」这条最常见的 happy-path 偏差（失效模式 #1）。
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type AsyncState<T> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly data: T }
  | { readonly kind: 'failed'; readonly error: unknown };

export interface AsyncResult<T> {
  readonly state: AsyncState<T>;
  /** 手动重试；Error 态的「重试」按钮接这里 */
  readonly reload: () => void;
  /** 是否正在后台刷新（已有数据时重新拉取，不闪回骨架屏） */
  readonly refreshing: boolean;
}

export function useAsync<T>(loader: () => Promise<T>, deps: readonly unknown[]): AsyncResult<T> {
  const [state, setState] = useState<AsyncState<T>>({ kind: 'loading' });
  const [refreshing, setRefreshing] = useState(false);
  const [nonce, setNonce] = useState(0);
  /** 单调递增的请求序号：晚发早归的响应不得覆盖新结果（经典竞态） */
  const latest = useRef(0);
  const hasData = useRef(false);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    const ticket = ++latest.current;
    let cancelled = false;

    if (hasData.current) {
      setRefreshing(true);
    } else {
      setState({ kind: 'loading' });
    }

    loaderRef
      .current()
      .then((data) => {
        if (cancelled || ticket !== latest.current) return;
        hasData.current = true;
        setState({ kind: 'ready', data });
      })
      .catch((error: unknown) => {
        if (cancelled || ticket !== latest.current) return;
        hasData.current = false;
        setState({ kind: 'failed', error });
      })
      .finally(() => {
        if (cancelled || ticket !== latest.current) return;
        setRefreshing(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint 的 exhaustive-deps 不适用：deps 由调用方显式声明，loader 走 ref 避免闭包陷阱
  }, [...deps, nonce]);

  const reload = useCallback(() => {
    hasData.current = false;
    setNonce((n) => n + 1);
  }, []);

  return { state, reload, refreshing };
}
